const { batchIdToProductId, getManufacturerProductId } = require('./product-batch-util')
const keyBy = require('lodash/keyBy')

/**
 * Calculates total volume and price for a shipment.
 * For PSM tenant only: Uses manufacturer-specific product volumetrics when available,
 * and does not fall back to generic product volumetrics.
 * For other tenants: Uses generic product volumetrics only (manufacturer in batch ID is just metadata).
 * @param {Object} shipment - Shipment object with counts property
 * @param {Object} mainApi - Main API object with state.tenant and product.getByIds method
 * @returns {Promise<{totalVolume: number, totalPrice: number}>} Total volume and price
 */
const calculateShipmentVolumeAndPrice = async (shipment, mainApi) => {
  const tenant = mainApi && mainApi.state && mainApi.state.tenant
  // Only use manufacturer-specific products for PSM tenant (not sub-tenants like EPN, RMS, etc.)
  // Validate tenant is a string before calling toLowerCase to avoid runtime errors
  const useManufacturerSpecificProducts = tenant && typeof tenant === 'string' && tenant.toLowerCase() === 'psm'

  // Collect both generic and manufacturer-specific product IDs (only for PSM tenant)
  const productIds = new Set()
  const batchIdToManufacturerProductId = {}

  for (const batchId of Object.keys(shipment.counts)) {
    const genericProductId = batchIdToProductId(batchId)
    productIds.add(genericProductId)

    // Only try manufacturer-specific products for PSM tenant
    if (useManufacturerSpecificProducts) {
      const manufacturerProductId = getManufacturerProductId(batchId)
      // Guard: "unknown" manufacturer is not a real product id
      if (manufacturerProductId && manufacturerProductId !== 'product:unknown') {
        productIds.add(manufacturerProductId)
        batchIdToManufacturerProductId[batchId] = manufacturerProductId
      }
    }
  }

  const allProductsDoc = await mainApi.product.getByIds([...productIds])
  const productsById = keyBy(allProductsDoc, '_id')

  let totalVolume = 0
  let totalPrice = 0

  for (const batchId of Object.keys(shipment.counts)) {
    const { quantity } = shipment.counts[batchId]
    const genericProductId = batchIdToProductId(batchId)

    // Try manufacturer-specific product first (only for PSM tenant)
    const manufacturerProductId = useManufacturerSpecificProducts
      ? batchIdToManufacturerProductId[batchId]
      : null

    // For volume (PSM): prefer manufacturer-specific, and do not fall back to generic if manufacturer-specific has no volumetrics
    const manufacturerProduct = manufacturerProductId && productsById[manufacturerProductId]
      ? productsById[manufacturerProductId]
      : null
    const genericProduct = productsById[genericProductId]

    // unitVolume is considered present only if it's a positive number (> 0)
    const manufacturerUnitVolume = manufacturerProduct ? Number(manufacturerProduct.unitVolume) : 0

    const productForVolume = useManufacturerSpecificProducts
      ? ((manufacturerProduct && manufacturerUnitVolume > 0) ? manufacturerProduct : null)
      : genericProduct

    // IMPORTANT: Price must always come from generic product only (no manufacturer-specific pricing).
    // Preserve old falsy behavior: treat 0 as missing for unitPrice.
    const unitPrice = (genericProduct && genericProduct.unitPrice) ? genericProduct.unitPrice : 0

    if (genericProduct) {
      const unitVolume = (productForVolume && Number(productForVolume.unitVolume) > 0) ? Number(productForVolume.unitVolume) : 0
      totalVolume += quantity * unitVolume
      totalPrice += quantity * unitPrice
    } else {
      console.error(`Product not found for shipment ${shipment.id}, batchId: ${batchId}, tried: ${manufacturerProductId || 'N/A'} and ${genericProductId}`)
    }
  }
  return { totalVolume, totalPrice }
}
exports.calculateShipmentVolumeAndPrice = calculateShipmentVolumeAndPrice
